import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContaDigital } from '@ibk/shared-config';
import { useTranslation } from '@ibk/shared-translation';
import { PixType } from '../../enums';
import { formatAsRegionalPhoneCode } from '../../utils/pixFormatters';
import { toPixKeyTypeDescription } from '../../utils/pixKeyUtils';
import { useAddFavoredContact } from '../useAddFavoredContact';
import { usePostPix } from '../usePostPix';
export function usePixTransferExecution({ transferData, onError, onPinModalClose, }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { postPix, pix, pixScheduled, pixError, pixAgencyAccountError, pixAgencyAccount, pixScheduledError, loading } = usePostPix();
    const { addFavoredContact, loading: contactLoading } = useAddFavoredContact();
    const { refetch: contaDigitalRefetch } = useContaDigital();
    const [showPinModal, setShowPinModal] = useState(false);
    const [localError, setLocalError] = useState();
    const error = localError;
    const handleAddContactAfterSuccess = useCallback(async () => {
        if (transferData.addToContactList && transferData.pixKeyDetails) {
            try {
                await addFavoredContact({
                    keyType: transferData.keyType,
                    keyValue: transferData.keyValue,
                    pixKeyDetails: transferData.pixKeyDetails,
                });
            }
            catch (err) {
                console.warn('Falha ao adicionar contato à lista de favoritos:', err);
            }
        }
    }, [
        transferData.addToContactList,
        transferData.pixKeyDetails,
        transferData.keyType,
        transferData.keyValue,
        addFavoredContact,
    ]);
    const handleNavigateToSuccess = useCallback((pixData, transferData) => {
        contaDigitalRefetch();
        navigate('/conta-pj/pix/sucesso', {
            state: { pixData, transferData },
        });
    }, [navigate]);
    const handleNavigateToScheduledSuccess = useCallback((scheduledData, transferData) => {
        contaDigitalRefetch();
        navigate('/conta-pj/pix/sucesso', {
            state: { scheduledData, transferData },
        });
    }, [navigate]);
    useEffect(() => {
        if (pix) {
            setShowPinModal(false);
            handleAddContactAfterSuccess().then(() => {
                handleNavigateToSuccess(pix, transferData);
            });
        }
    }, [pix, handleAddContactAfterSuccess, handleNavigateToSuccess, transferData]);
    useEffect(() => {
        if (pixAgencyAccount) {
            setShowPinModal(false);
            handleAddContactAfterSuccess().then(() => {
                handleNavigateToSuccess(pixAgencyAccount, transferData);
            });
        }
    }, [pixAgencyAccount, handleAddContactAfterSuccess, handleNavigateToSuccess, transferData]);
    useEffect(() => {
        if (pixScheduled) {
            setShowPinModal(false);
            handleAddContactAfterSuccess().then(() => {
                handleNavigateToScheduledSuccess(pixScheduled, transferData);
            });
        }
    }, [pixScheduled, handleAddContactAfterSuccess, handleNavigateToScheduledSuccess, transferData]);
    useEffect(() => {
        if ((pix || pixScheduled || pixAgencyAccount) && showPinModal) {
            setShowPinModal(false);
        }
    }, [pix, pixScheduled, pixAgencyAccount, showPinModal]);
    useEffect(() => {
        if (transferData.confirmDuplicate && !pix && !pixScheduled && !pixAgencyAccount && !showPinModal) {
            setShowPinModal(true);
        }
    }, [transferData.confirmDuplicate, pix, pixScheduled, pixAgencyAccount, showPinModal]);
    useEffect(() => {
        if (pixError && onError) {
            setShowPinModal(false);
            onError(pixError);
        }
    }, [pixError, onError]);
    useEffect(() => {
        if (pixAgencyAccountError && onError) {
            setShowPinModal(false);
            onError(pixAgencyAccountError);
        }
    }, [pixAgencyAccountError, onError]);
    useEffect(() => {
        if (pixScheduledError && onError) {
            setShowPinModal(false);
            onError(pixScheduledError);
        }
    }, [pixScheduledError, onError]);
    function convertToGenericPixKey(data) {
        const key = data.keyType === PixType.PHONE ? formatAsRegionalPhoneCode(data.keyValue) : data.keyValue;
        const keyType = toPixKeyTypeDescription(data.keyType, data.keyValue);
        const endToEndIdFromKeyLookup = data.pixKeyDetails?.endToEndId?.trim();
        return {
            key,
            type: keyType,
            amount: data.value,
            receiverName: data.pixKeyDetails?.name ?? data.pixAgencyAccount?.name,
            receiverDocumentNumber: data.pixKeyDetails?.taxIdentifier ?? data.pixAgencyAccount?.document,
            description: '',
            bankName: data.pixKeyDetails?.bank ?? data.pixAgencyAccount?.bankName,
            account: data.pixKeyDetails?.account ?? data.pixAgencyAccount?.bankAccount,
            branch: data.pixKeyDetails?.branch ?? data.pixAgencyAccount?.bankBranch,
            bankCode: data.pixKeyDetails?.bankId ?? data.pixAgencyAccount?.bankCode,
            accountType: data.pixKeyDetails?.accountType,
            accountTypeAcronym: data.pixAgencyAccount?.accountType,
            endToEndId: endToEndIdFromKeyLookup ?? undefined,
            confirmDuplicate: data.confirmDuplicate,
        };
    }
    function openPinModal() {
        setShowPinModal(true);
    }
    function closePinModal() {
        setShowPinModal(false);
        if (onPinModalClose) {
            onPinModalClose();
        }
    }
    function clearError() {
        setLocalError(undefined);
    }
    async function transferPix(pin) {
        try {
            setLocalError(undefined);
            const genericPixKey = convertToGenericPixKey(transferData);
            const transactionStartAt = new Date().toISOString();
            await postPix(pin, transactionStartAt, transferData.paymentDate, genericPixKey);
        }
        catch (err) {
            setLocalError(t('transaction-not-done-try-again-later'));
            closePinModal();
        }
    }
    return {
        loading: loading || contactLoading,
        error,
        showPinModal,
        transferPix,
        closePinModal,
        openPinModal,
        clearError,
    };
}
